import { Alert, Button } from '@mui/material'
import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import {
  getVisitDashboardGeneralCargoInfoByOrders,
  getVisitDashboardUnitInfoByOrders,
} from '@planning/utils/order-utils'
import { useTranslate } from '@tolgee/react'
import { PATHS } from '@tom-ui/utils'
import _ from 'lodash'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { VisitDashboardDirectionCard } from './VisitDashboardDirectionCard'

interface Props {
  orders: IOrderItem[]
  cargoType?: CargoType
  visitId?: number
  visitType?: CarrierType
  visitStatus?: CarrierVisitStatus
  cardFooter?: ReactNode
  isMinimalisticView?: boolean
  isTallyPage?: boolean
  onViewClick?: () => void
  onClickExportOrders?: (direction: CarrierVisitDirection) => void
}
export const OutboundDashboardCard = observer(
  ({
    orders,
    cargoType,
    visitId,
    visitType,
    visitStatus,
    cardFooter,
    isMinimalisticView,
    isTallyPage,
    onViewClick,
    onClickExportOrders,
  }: Props) => {
    const { t } = useTranslate()
    const navigate = useNavigate()
    const { nnrOrderItemStore } = usePlanningStore()

    const { totalDefinition, archetypeGroups } = computed(() => {
      if (cargoType && cargoType === CargoType.GeneralCargo) {
        return {
          totalDefinition: getVisitDashboardGeneralCargoInfoByOrders(orders).totalDefinition,
          archetypeGroups: [],
        }
      } else {
        return getVisitDashboardUnitInfoByOrders(orders)
      }
    }).get()

    const nnrOrderSummary = _.get(nnrOrderItemStore.orderSummaryForVesselVisitId, visitId!)
    const handleAssignNnrOrder = () => {
      if (!!nnrOrderSummary && nnrOrderSummary.nnrOrders.length === 1) {
        navigate(
          `${PATHS.orders.nonNumericReleases.url}/${nnrOrderSummary.nnrOrders[0].id}/details`,
        )
      } else {
        navigate(`${PATHS.orders.nnrForVisit.url}/${visitId}`)
      }
    }

    const getUnassignedReleasesAlertText = (totalAmount: number, totalUnassigned: number) => {
      return `${totalAmount} ${
        totalUnassigned > 1
          ? t('containerPlannedForNonNumericLoadidng', 'containers planned for non-numeric loading')
          : t('containerPlannedForNonNumericLoadidng', 'container planned for non-numeric loading')
      } (${totalUnassigned} ${t('notAssignedYet', 'not assigned yet')})`
    }

    return (
      <VisitDashboardDirectionCard
        title={t('load', 'Load')}
        cargoType={cargoType}
        totalDefinition={totalDefinition}
        direction={CarrierVisitDirection.Outbound}
        visitId={visitId}
        visitType={visitType}
        visitStatus={visitStatus}
        archetypeGroups={archetypeGroups}
        isMinimalisticView={isMinimalisticView}
        isTallyPage={isTallyPage}
        onViewClick={onViewClick}
        onClickExportOrders={
          onClickExportOrders
            ? () => onClickExportOrders(CarrierVisitDirection.Outbound)
            : undefined
        }
        additionalContent={
          <>
            {nnrOrderSummary?.totalUnassignedContainersForVisit > 0 && (
              <Alert
                data-cy='unassigned-nnr-orders-alert'
                severity='warning'
                action={
                  <Button color='inherit' size='small' onClick={handleAssignNnrOrder}>
                    {t('assign', 'Assign')}
                  </Button>
                }
              >
                {getUnassignedReleasesAlertText(
                  nnrOrderSummary.totalAmount,
                  nnrOrderSummary.totalUnassignedContainersForVisit,
                )}
              </Alert>
            )}
          </>
        }
        cardFooter={cardFooter}
      />
    )
  },
)
